/* eslint-disable react/prop-types */
import { Formik, Form } from "formik";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Components & Services
import { SalesDetailedLedgerInfo } from "./info";
import { SalesDetailedLedgerService } from "services/sales-detailed-ledger";
import { getPrintTemplate } from "./components/printTemplate";

const today = new Date();

const initialValues = {
  unit: "",
  address: "",
  productName: "",
  fiscalYear: today.getFullYear().toString(),
  bookNumber: "01",
  details: [
    {
      bookingDate: "",
      documentNumber: "",
      documentDate: "",
      description: "",
      correspondingAccount: "",
      quantity: 0,
      unitPrice: 0,
      amount: 0,
      taxAmount: 0,
      deductionAmount: 0,
    }
  ],
  totalIncurred: {
    quantity: 0,
    amount: 0,
    taxAmount: 0,
    deductionAmount: 0,
  },
  netRevenue: 0,
  costOfGoodsSold: 0,
  grossProfit: 0,
  totalPages: "1",
  openingDate: "",
  reportDate: "",
  ledgerClerk: "",
  chiefAccountant: "",
  legalRepresentative: "",
  // UI helpers for signatures
  signingDay: today.getDate().toString(),
  signingMonth: (today.getMonth() + 1).toString(),
  signingYear: today.getFullYear().toString(),
};

function SalesDetailedLedgerDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();
  const [formInitials, setFormInitials] = useState(initialValues);

  useEffect(() => {
    (async () => {
      try {
        if (id) {
          const response = await SalesDetailedLedgerService.getById(id);
          const apiData = response.data ? response.data : response;

          const formatDateVN = (isoString) => {
            if (!isoString) return "";
            if (typeof isoString === "string" && isoString.includes("/")) return isoString;
            const d = new Date(isoString);
            if (isNaN(d.getTime())) return isoString;
            return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
          };

          // Parse reportDate -> signingDay/Month/Year
          let sDay = initialValues.signingDay;
          let sMonth = initialValues.signingMonth;
          let sYear = initialValues.signingYear;
          const rDate = apiData.reportDate || "";
          if (rDate) {
            const parts = rDate.split("/");
            if (parts.length === 3) {
              sDay = parts[0];
              sMonth = parts[1];
              sYear = parts[2];
            }
          }

          setFormInitials({
            ...initialValues,
            unit: apiData.unit || apiData.company?.name || "",
            address: apiData.address || apiData.company?.address || "",
            productName: apiData.productName || "",
            fiscalYear: apiData.fiscalYear || today.getFullYear().toString(),
            bookNumber: apiData.bookNumber || "01",
            details: (apiData.details || []).map(d => ({
                ...d,
                documentDate: formatDateVN(d.documentDate)
            })),
            totalIncurred: apiData.totalIncurred || initialValues.totalIncurred,
            netRevenue: apiData.netRevenue || 0,
            costOfGoodsSold: apiData.costOfGoodsSold || 0,
            grossProfit: apiData.grossProfit || 0,
            totalPages: apiData.totalPages || "1",
            openingDate: formatDateVN(apiData.openingDate),
            reportDate: rDate,
            signingDay: sDay,
            signingMonth: sMonth,
            signingYear: sYear,
            ledgerClerk: apiData.ledgerClerk || "",
            chiefAccountant: apiData.chiefAccountant || "",
            legalRepresentative: apiData.legalRepresentative || "",
          });
        }
      } catch (error) {
        console.error("Lỗi load Sổ S35-DN:", error);
        toast.error("Không thể tải dữ liệu");
      }
    })();
  }, [id]);

  const handleBack = () => navigation(-1);

  const submitForm = async (fValues) => {
    try {
      const num = (val) => (val && val !== "" && !isNaN(val) ? Number(val) : 0);

      const payload = {
        unit: fValues.unit || "",
        address: fValues.address || "",
        productName: fValues.productName || "",
        fiscalYear: fValues.fiscalYear || "",
        bookNumber: fValues.bookNumber || "",
        details: (fValues.details || []).map(d => ({
          bookingDate: d.bookingDate || "",
          documentNumber: d.documentNumber || "",
          documentDate: d.documentDate || "",
          description: d.description || "",
          correspondingAccount: d.correspondingAccount || "",
          quantity: num(d.quantity),
          unitPrice: num(d.unitPrice),
          amount: num(d.amount),
          taxAmount: num(d.taxAmount),
          deductionAmount: num(d.deductionAmount),
        })),
        totalIncurred: {
          quantity: num(fValues.totalIncurred.quantity),
          amount: num(fValues.totalIncurred.amount),
          taxAmount: num(fValues.totalIncurred.taxAmount),
          deductionAmount: num(fValues.totalIncurred.deductionAmount),
        },
        netRevenue: num(fValues.netRevenue),
        costOfGoodsSold: num(fValues.costOfGoodsSold),
        grossProfit: num(fValues.grossProfit),
        totalPages: fValues.totalPages?.toString() || "1",
        openingDate: fValues.openingDate || "",
        reportDate: `${fValues.signingDay.padStart(2, "0")}/${fValues.signingMonth.padStart(2, "0")}/${fValues.signingYear}`,
        ledgerClerk: fValues.ledgerClerk || "",
        chiefAccountant: fValues.chiefAccountant || "",
        legalRepresentative: fValues.legalRepresentative || "",
      };

      console.log("🚀 SUBMIT PAYLOAD S35-DN:", payload);

      if (!id) {
        await SalesDetailedLedgerService.create(payload);
        toast.success("Tạo sổ thành công!");
        navigation("/templates/sales-detailed-ledger");
      } else {
        await SalesDetailedLedgerService.update(id, payload);
        toast.success("Cập nhật thành công!");
      }
    } catch (error) {
      console.error("Lỗi lưu:", error);
      toast.error(error?.response?.data?.message || "Lưu thất bại!");
    }
  };

  const handlePrint = (pValues) => {
    const html = getPrintTemplate(pValues);
    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(`
      <html>
        <head>
          <title>In Sổ chi tiết bán hàng S35-DN</title>
          <style>@media print { @page { size: A4 landscape; margin: 1cm; } }</style>
        </head>
        <body onload="setTimeout(() => [window.print(), window.close()], 500)">${html}</body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={20}>
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Formik initialValues={formInitials} enableReinitialize onSubmit={submitForm}>
              {({ values, isSubmitting, setFieldValue }) => (
                <Form autoComplete="off">
                  <Card sx={{ height: "100%" }}>
                    <SoftBox p={2}>
                      <SalesDetailedLedgerInfo
                        formData={{ values, setFieldValue, containView }}
                      />
                      <SoftBox mt={10} display="flex" justifyContent="space-between">
                        <SoftButton variant="gradient" color="light" onClick={handleBack}>
                          Trở về
                        </SoftButton>
                        <SoftBox display="flex" gap={2}>
                          {containView ? (
                            <SoftButton variant="gradient" color="success" onClick={() => handlePrint(values)}>
                              In Sổ S35-DN
                            </SoftButton>
                          ) : (
                            <SoftButton disabled={isSubmitting} type="submit" variant="gradient" color="dark">
                              {id ? "Lưu" : "Tạo mới"}
                            </SoftButton>
                          )}
                        </SoftBox>
                      </SoftBox>
                    </SoftBox>
                  </Card>
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}

export default SalesDetailedLedgerDetail;
